import { useEffect, useState } from 'react';
import type { AdOffer } from '@/lib/ads-campaigns';
import {
  submitAdCampaign,
  uploadCampaignBanner,
  OFFER_PLACEMENT,
  initiatePaydunyaCheckout,
  fetchCurrentUserProfileLink,
} from '@/lib/ads-campaigns';
import { useAuth } from '@/context/AuthContext';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';

const SECTORS = [
  'Commerce / Distribution',
  'Technologie / Numérique',
  'Finance / Banque',
  'Santé',
  'Éducation / Formation',
  'Agroalimentaire / Agriculture',
  'Mode / Beauté',
  'Tourisme / Hôtellerie',
  'Transport / Logistique',
  'Médias / Communication',
  'Immobilier / Construction',
  'Autre',
];

const DURATIONS = [
  { value: 30, label: '1 mois (30 jours)' },
  { value: 60, label: '2 mois (60 jours)' },
  { value: 90, label: '3 mois (90 jours)' },
];

const EMPTY_FORM = {
  companyName: '',
  managerName: '',
  phone: '',
  email: '',
  sector: '',
  website: '',
  companyPageUrl: '',
  startDate: '',
  durationDays: 30,
};

interface SubscribeModalProps {
  offer: AdOffer | null;
  onClose: () => void;
}

function SubscribeModal({ offer, onClose }: SubscribeModalProps) {
  const { user } = useAuth();
  const { copied, copy } = useCopyToClipboard();
  const [form, setForm] = useState(EMPTY_FORM);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [honeypot, setHoneypot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [payLoading, setPayLoading] = useState(false);
  const [payError, setPayError] = useState('');
  const [profileLink, setProfileLink] = useState<string | null>(null);
  const [profileLinkLoading, setProfileLinkLoading] = useState(false);

  useEffect(() => {
    if (offer) {
      setForm(EMPTY_FORM);
      setBannerFile(null);
      setError('');
      setSuccess(false);
      setHoneypot('');
      setCampaignId(null);
      setPayLoading(false);
      setPayError('');
      setProfileLink(null);
    }
  }, [offer]);

  useEffect(() => {
    if (!offer || !user) return;
    let mounted = true;
    setProfileLinkLoading(true);
    fetchCurrentUserProfileLink(user.id)
      .then((res) => {
        if (mounted && res.url) setProfileLink(res.url);
      })
      .catch(() => {})
      .finally(() => {
        if (mounted) setProfileLinkLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [offer, user]);

  const handleCopyProfileLink = async () => {
    if (!profileLink) return;
    await copy(profileLink);
    update('companyPageUrl', profileLink);
  };

  if (!offer) return null;

  const update = (key: keyof typeof EMPTY_FORM, value: string | number) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    // Honeypot anti-spam : si rempli, on simule le succès sans rien envoyer.
    if (honeypot.trim()) {
      setSuccess(true);
      return;
    }

    if (!form.companyName.trim() || !form.managerName.trim() || !form.phone.trim() || !form.email.trim()) {
      setError('Veuillez renseigner tous les champs obligatoires.');
      return;
    }
    if (!bannerFile) {
      setError("Veuillez téléverser la bannière publicitaire de votre campagne.");
      return;
    }

    setSubmitting(true);
    try {
      let bannerUrl: string | null = null;
      if (bannerFile) {
        bannerUrl = await uploadCampaignBanner(bannerFile);
      }

      const campaign = await submitAdCampaign({
        companyName: form.companyName,
        managerName: form.managerName,
        phone: form.phone,
        email: form.email,
        sector: form.sector,
        website: form.website,
        companyPageUrl: form.companyPageUrl,
        offer: offer.key,
        bannerUrl,
        startDate: form.startDate,
        durationDays: form.durationDays,
        placement: OFFER_PLACEMENT[offer.key],
      });

      setCampaignId(campaign.id);
      setSuccess(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Une erreur est survenue lors de l'envoi. Veuillez réessayer.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayNow = async () => {
    if (!campaignId || !offer) return;
    setPayLoading(true);
    setPayError('');

    try {
      const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
      const prefix = basePath ? `/${basePath}` : '';
      const origin = window.location.origin;

      const { checkoutUrl } = await initiatePaydunyaCheckout(
        campaignId,
        offer,
        form.durationDays,
        form.companyName,
        form.email,
        form.phone,
        `${origin}${prefix}/ads/payment-cancelled`,
        `${origin}${prefix}/ads/payment-callback`,
      );

      window.location.href = checkoutUrl;
    } catch (err) {
      setPayError(
        err instanceof Error
          ? err.message
          : 'Erreur lors de la préparation du paiement',
      );
      setPayLoading(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors';
  const labelClass = 'block text-xs font-medium text-foreground-300 mb-1.5';

  return (
    <div className="fixed inset-0 z-[100] flex items-start md:items-center justify-center p-4 md:p-6">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto no-scrollbar rounded-2xl bg-background-100 border border-background-200/70 shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-background-100/95 backdrop-blur-md border-b border-background-200/70">
          <div>
            <h2 className="font-heading font-bold text-lg text-foreground-50">
              Souscrire à l'offre {offer.name}
            </h2>
            <p className="text-xs text-foreground-400 mt-0.5">
              {offer.price.toLocaleString('fr-FR')} FCFA / mois — Publicité & Sponsoring
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-200/60 text-foreground-400 hover:text-foreground-50 transition-colors shrink-0"
            aria-label="Fermer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {success ? (
          <div className="px-6 py-10 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-500/15">
              <i className="ri-checkbox-circle-line text-4xl text-accent-400" />
            </div>
            <h3 className="font-heading font-bold text-xl text-foreground-50 mb-2">
              Demande bien reçue !
            </h3>
            <p className="text-sm text-foreground-400 max-w-md mx-auto leading-relaxed">
              Merci {form.managerName.trim() || ''}. Votre demande de campagne{' '}
              <strong className="text-foreground-50">{offer.name}</strong> a bien été envoyée. Pour
              que votre campagne soit validée et diffusée,{' '}
              <strong className="text-foreground-50">le paiement est obligatoire</strong>.
            </p>

            {payError && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-accent-100/10 border border-accent-400/20 mb-4 mt-4 mx-auto max-w-md text-left">
                <i className="ri-error-warning-line text-accent-400 mt-0.5 shrink-0" />
                <p className="text-sm text-accent-400">{payError}</p>
              </div>
            )}

            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              {campaignId && (
                <button
                  type="button"
                  onClick={handlePayNow}
                  disabled={payLoading}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary-500 text-foreground-950 font-semibold text-sm hover:bg-primary-400 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  {payLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Préparation du paiement…
                    </>
                  ) : (
                    <>
                      <i className="ri-bank-card-line" />
                      Payer maintenant
                    </>
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-background-200 text-foreground-50 font-semibold text-sm hover:bg-background-300 transition-colors whitespace-nowrap"
              >
                Payer plus tard
              </button>
            </div>
            {campaignId && (
              <p className="text-xs text-foreground-500 mt-3">
                Tant que le paiement n'est pas confirmé, votre campagne restera en attente et ne
                pourra pas être diffusée.
              </p>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-5" noValidate>
            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-accent-100/10 border border-accent-400/20">
                <i className="ri-error-warning-line text-accent-400 mt-0.5 shrink-0" />
                <p className="text-sm text-accent-400">{error}</p>
              </div>
            )}

            {/* Honeypot anti-spam (caché) */}
            <input
              type="text"
              name="website_alt"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              className="website_alt_field"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly={false}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="ads-company" className={labelClass}>
                  Nom de l'entreprise *
                </label>
                <input
                  id="ads-company"
                  name="company_name"
                  type="text"
                  value={form.companyName}
                  onChange={(e) => update('companyName', e.target.value)}
                  placeholder="Ex : Maison Wax Royale"
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label htmlFor="ads-manager" className={labelClass}>
                  Nom du responsable *
                </label>
                <input
                  id="ads-manager"
                  name="manager_name"
                  type="text"
                  value={form.managerName}
                  onChange={(e) => update('managerName', e.target.value)}
                  placeholder="Ex : Awa Koffi"
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label htmlFor="ads-phone" className={labelClass}>
                  Téléphone *
                </label>
                <input
                  id="ads-phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  placeholder="Ex : +228 90 00 00 00"
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label htmlFor="ads-email" className={labelClass}>
                  Email *
                </label>
                <input
                  id="ads-email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  placeholder="ex : contact@entreprise.com"
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label htmlFor="ads-sector" className={labelClass}>
                  Secteur d'activité
                </label>
                <select
                  id="ads-sector"
                  name="sector"
                  value={form.sector}
                  onChange={(e) => update('sector', e.target.value)}
                  className={inputClass}
                >
                  <option value="">Sélectionner un secteur</option>
                  {SECTORS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="ads-website" className={labelClass}>
                  Site web
                </label>
                <input
                  id="ads-website"
                  name="website"
                  type="url"
                  value={form.website}
                  onChange={(e) => update('website', e.target.value)}
                  placeholder="https://…"
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label htmlFor="ads-pageurl" className={labelClass}>
                Lien de la page de l'entreprise (WorldApeiron)
              </label>
              <div className="flex items-stretch gap-2">
                <input
                  id="ads-pageurl"
                  name="company_page_url"
                  type="url"
                  value={form.companyPageUrl}
                  onChange={(e) => update('companyPageUrl', e.target.value)}
                  placeholder="https://worldapeiron.com/business/…"
                  className={inputClass}
                />
                {user && (
                  <button
                    type="button"
                    onClick={handleCopyProfileLink}
                    disabled={profileLinkLoading || !profileLink}
                    className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-secondary-500 text-foreground-950 font-semibold text-sm hover:bg-secondary-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                    title={
                      profileLink
                        ? 'Copier le lien de votre profil'
                        : 'Aucun profil public (entreprise ou freelance) trouvé'
                    }
                  >
                    {profileLinkLoading ? (
                      <i className="ri-loader-4-line animate-spin" />
                    ) : (
                      <i className={copied ? 'ri-check-line' : 'ri-link'} />
                    )}
                    {copied ? 'Copié !' : 'Mon lien'}
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="ads-start" className={labelClass}>
                  Date souhaitée de début *
                </label>
                <input
                  id="ads-start"
                  name="start_date"
                  type="date"
                  value={form.startDate}
                  onChange={(e) => update('startDate', e.target.value)}
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label htmlFor="ads-duration" className={labelClass}>
                  Durée de la campagne
                </label>
                <select
                  id="ads-duration"
                  name="duration_days"
                  value={form.durationDays}
                  onChange={(e) => update('durationDays', Number(e.target.value))}
                  className={inputClass}
                >
                  {DURATIONS.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className={labelClass}>Bannière publicitaire *</label>
              {!bannerFile ? (
                <label className="flex flex-col items-center justify-center gap-2 py-6 px-4 rounded-lg border-2 border-dashed border-background-300/60 hover:border-primary-400/50 cursor-pointer transition-colors">
                  <i className="ri-image-add-line text-2xl text-foreground-500" />
                  <span className="text-sm text-foreground-400 text-center">
                    Cliquez pour téléverser votre bannière (image)
                  </span>
                  <input
                    type="file"
                    name="banner"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => setBannerFile(e.target.files?.[0] || null)}
                  />
                </label>
              ) : (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-500/10 shrink-0 overflow-hidden">
                    <img
                      src={URL.createObjectURL(bannerFile)}
                      alt="Aperçu de la bannière"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-foreground-50 truncate">{bannerFile.name}</p>
                    <p className="text-xs text-foreground-400">
                      {(bannerFile.size / 1024).toFixed(0)} Ko
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setBannerFile(null)}
                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-200/60 text-foreground-500 transition-colors shrink-0"
                    aria-label="Retirer la bannière"
                  >
                    <i className="ri-close-line" />
                  </button>
                </div>
              )}
            </div>

            <div className="pt-1">
              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-primary-500 text-foreground-950 font-semibold text-sm hover:bg-primary-400 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Envoi en cours…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line" />
                    Envoyer ma demande
                  </>
                )}
              </button>
              <p className="text-xs text-foreground-500 text-center mt-3">
                En envoyant, vous acceptez d'être recontacté par l'équipe WorldApeiron.
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default SubscribeModal;